import React, { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	type ModalHeaderProps,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';

import { VOID_FUNC } from '../../utils/constants';
import { triggerAnalyticsForCloseTeamCreateDialog } from '../analytics';
import TeamCreate from '../TeamCreate';

import { type TeamCreateModalProps } from './types';

const DIALOG_WIDTH = 480;

const TeamCreateModal = (props: TeamCreateModalProps) => {
	const { extraAnalyticsAttrs, isBlanketHidden, onClose = VOID_FUNC } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireEvent } = useAnalyticsEventsNext();

	// Work-around:
	// When TeamCreateDialog renders inside PeopleMenu and the viewport is narrow,
	// the dialog is closed improperly when users click anywhere in the dialog
	const preventCloseDialog = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
	};

	const closeDialog = useCallback(() => {
		if (fg('ptc-enable-teams-common-analytics-refactor')) {
			fireEvent('ui.teamCreateDialog.closed', extraAnalyticsAttrs || {});
		} else {
			triggerAnalyticsForCloseTeamCreateDialog(createAnalyticsEvent, extraAnalyticsAttrs);
		}
		onClose();
	}, [createAnalyticsEvent, extraAnalyticsAttrs, onClose, fireEvent]);

	const [isLoading, setLoading] = useState(false);

	const innerSetLoading = useCallback(
		(isLoading: boolean) => {
			setLoading(isLoading);
			(props.setIsLoading || VOID_FUNC)(isLoading);
		},
		[props.setIsLoading],
	);

	const customComponents = {
		...props.customComponents,
		header:
			props.customComponents?.header ||
			props.customComponents?.modalHeader ||
			fg('enable_team_creation_modal_header_prop_change')
				? (props: ModalHeaderProps) => <ModalHeader {...props} hasCloseButton={false} />
				: ModalHeader,
		title: props.customComponents?.title || props.customComponents?.modalTitle || ModalTitle,
		body: props.customComponents?.body || props.customComponents?.modalBody || ModalBody,
		footer: props.customComponents?.footer || props.customComponents?.modalFooter || ModalFooter,
	};

	const customMessages = {
		...props.customMessages,
		title: props.customMessages?.title || props.customMessages?.modalTitle || undefined,
	};

	return (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div onClick={preventCloseDialog}>
			<ModalDialog
				shouldScrollInViewport
				onClose={closeDialog}
				width={DIALOG_WIDTH}
				isBlanketHidden={isBlanketHidden}
				shouldCloseOnOverlayClick={!isLoading}
				shouldCloseOnEscapePress={!isLoading}
			>
				<TeamCreate
					{...props}
					onClose={closeDialog}
					setIsLoading={innerSetLoading}
					customComponents={customComponents}
					customMessages={customMessages}
				/>
			</ModalDialog>
		</div>
	);
};

export default TeamCreateModal;
